import { openHeroReport } from '@/components/hero-award'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { DollarSignIcon, FileTextIcon, MedalIcon, MegaphoneIcon, UsersIcon } from 'lucide-react'
import {
  useFinanceCsv,
  useMemberAwardCards,
  useMemberResultsEmailPreview,
  useResultsCsv,
  useSendMemberResultsEmails,
} from '../api/hooks'
import { toast } from 'sonner'
import { useCurrentShow } from '@/context/current-show'
import { CurrentShowSummary, SelectShowNotice } from '@/components/current-show'
import { useRunMode } from '@/context/run-mode'
import { PageHeading } from '@/components/page-heading'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useShowSlug } from '@/hooks/useShowSlug'
import { showAware } from '@/lib/paths'
import { ReleaseControls } from '@/features/show-release/ReleaseControls'

export default function ReportsPage() {
  const { mode } = useRunMode()
  const { currentShow } = useCurrentShow()
  const currentSlug = useShowSlug()
  const [results, setResults] = useState('')
  const [finance, setFinance] = useState('')
  const [emailSheetOpen, setEmailSheetOpen] = useState(false)
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<Set<string>>(new Set())
  const [previewExhibitorId, setPreviewExhibitorId] = useState<string | null>(null)

  const resultsCsv = useResultsCsv()
  const financeCsv = useFinanceCsv()
  const memberAwardCardsQuery = useMemberAwardCards(currentShow?.id)
  const sendMemberEmails = useSendMemberResultsEmails()
  const emailPreviewQuery = useMemberResultsEmailPreview(
    currentShow?.id,
    previewExhibitorId ?? undefined
  )

  const memberCardData = memberAwardCardsQuery.data
  const memberRecipients = useMemo(
    () => memberCardData?.recipients ?? [],
    [memberCardData?.recipients]
  )
  const selectableRecipients = useMemo(
    () => memberRecipients.filter(recipient => Boolean(recipient.email)),
    [memberRecipients]
  )
  const memberPlacementsCount = memberCardData?.cards.length ?? 0
  const memberEmailSelectedCount = selectedRecipientIds.size
  const memberTotalPoints = useMemo(
    () => memberRecipients.reduce((sum, recipient) => sum + recipient.totalPoints, 0),
    [memberRecipients]
  )

  useEffect(() => {
    if (emailSheetOpen) {
      const validIdSet = new Set(selectableRecipients.map(recipient => recipient.exhibitorId))
      setSelectedRecipientIds(prev => {
        if (validIdSet.size === 0) {
          return new Set()
        }
        if (prev.size === 0) {
          return new Set(validIdSet)
        }
        const next = new Set([...prev].filter(id => validIdSet.has(id)))
        if (next.size === 0) {
          return new Set(validIdSet)
        }
        return next
      })

      if (
        memberRecipients.length > 0 &&
        (!previewExhibitorId || !memberRecipients.some(r => r.exhibitorId === previewExhibitorId))
      ) {
        setPreviewExhibitorId(memberRecipients[0]?.exhibitorId ?? null)
      }

      if (memberRecipients.length === 0) {
        setPreviewExhibitorId(null)
      }
    } else {
      setPreviewExhibitorId(null)
      setSelectedRecipientIds(new Set())
    }
  }, [emailSheetOpen, memberRecipients, selectableRecipients, previewExhibitorId])

  const handleOpenShowResultsReport = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the Show Results Report.')
      return
    }

    const url = new URL('/print/reports/sharing', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the Show Results Report.')
    }
  }, [currentShow])

  const handleOpenJudgeReport = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the results announcement.')
      return
    }

    const url = new URL('/print/reports/judge-speaking', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the results announcement.')
    }
  }, [currentShow])

  const handleOpenDisplayReport = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the display results.')
      return
    }

    const url = new URL('/print/reports/display', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the display results report.')
    }
  }, [currentShow])

  const handleOpenClubParticipationPrint = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the club participation report.')
      return
    }

    const url = new URL('/print/reports/club-participation', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the club participation report.')
    }
  }, [currentShow])

  const handleOpenStaffRoster = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before opening the staff roster.')
      return
    }

    const url = new URL('/print/reports/staff-roster', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the staff roster.')
    }
  }, [currentShow])

  const handleOpenStaffCertificates = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before printing staff certificates.')
      return
    }

    const url = new URL('/print/reports/certificates/staff', window.location.origin)
    url.searchParams.set('showId', currentShow.id)

    const showName = currentShow.name?.trim()
    if (showName) {
      url.searchParams.set('showName', showName)
    }

    const showLocation = currentShow.location?.trim()
    if (showLocation) {
      url.searchParams.set('showLocation', showLocation)
    }

    if (currentShow.startDate) {
      url.searchParams.set('showStartDate', currentShow.startDate)
    }

    if (currentShow.endDate) {
      url.searchParams.set('showEndDate', currentShow.endDate)
    }

    const popup = window.open(url.toString(), '_blank', 'noopener')
    if (!popup) {
      toast.error('Allow pop-ups to open the staff certificates.')
    }
  }, [currentShow])

  const handleGenerateReports = async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before generating reports.')
      return
    }
    setResults('')
    setFinance('')
    try {
      const [resultsData, financeData] = await Promise.all([
        resultsCsv.mutateAsync({ showId: currentShow.id }),
        financeCsv.mutateAsync({ showId: currentShow.id }),
      ])
      setResults(resultsData)
      setFinance(financeData)
      toast.success(`Reports generated for ${currentShow.name}`)
    } catch (_error) {
      toast.error('Failed to generate one or more reports')
    }
  }

  const handleDownloadCsv = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(`${filename} downloaded`)
  }

  const handleRecipientToggle = useCallback((exhibitorId: string) => {
    setSelectedRecipientIds(prev => {
      const next = new Set(prev)
      if (next.has(exhibitorId)) {
        next.delete(exhibitorId)
      } else {
        next.add(exhibitorId)
      }
      return next
    })
  }, [])

  const handleSelectAllRecipients = useCallback(() => {
    setSelectedRecipientIds(new Set(selectableRecipients.map(recipient => recipient.exhibitorId)))
  }, [selectableRecipients])

  const handleClearRecipients = useCallback(() => {
    setSelectedRecipientIds(new Set())
  }, [])

  const handleOpenEmailSheet = useCallback(() => {
    if (!currentShow?.id) {
      toast.error('Select a show before emailing members.')
      return
    }
    setEmailSheetOpen(true)
  }, [currentShow?.id])

  const handleSendMemberEmails = useCallback(async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before emailing members.')
      return
    }
    if (selectedRecipientIds.size === 0) {
      toast.error('Select at least one member to email.')
      return
    }

    try {
      const response = await sendMemberEmails.mutateAsync({
        showId: currentShow.id,
        payload: { exhibitorIds: Array.from(selectedRecipientIds) },
      })

      toast.success(
        `Sent ${response.sent} email${response.sent === 1 ? '' : 's'} (${response.skipped} skipped).`
      )
      setEmailSheetOpen(false)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send member emails')
    }
  }, [currentShow?.id, selectedRecipientIds, sendMemberEmails])

  if (!currentShow) {
    return (
      <SelectShowNotice description="Choose a show to generate finance and results CSV snapshots." />
    )
  }

  return (
    <>
      <div className="space-y-6">
        <header className="space-y-2">
          <PageHeading>Reports</PageHeading>
          <p className="text-muted-foreground">
            Generate CSV snapshots and member communications for the selected show.
          </p>
        </header>

        {mode !== 'run' && <CurrentShowSummary show={currentShow} />}
        {currentShow?.seriesMembership?.series && (
          <Card>
            <CardHeader>
              <CardTitle>Series trends</CardTitle>
              <CardDescription>
                Live annual participation and confirmed class trends across readable editions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link to={showAware('/series-report', { currentSlug })}>Open series trends</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {currentShow?.awardsPolicy?.heroAwardEnabled && (
          <Card>
            <CardHeader>
              <CardTitle>Hero Award Eligibility Report</CardTitle>
              <CardDescription>
                Current candidate Category IDs and Exhibit IDs, with tentative or ready status.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => openHeroReport(currentShow)}>Print eligibility report</Button>
            </CardContent>
          </Card>
        )}

        {currentShow?.id && (
          <Card>
            <CardHeader>
              <CardTitle>Public results</CardTitle>
              <CardDescription>
                Release this show's results to its public page after the awards are announced.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ReleaseControls key={currentShow.id} showId={currentShow.id} />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <FileTextIcon className="w-5 h-5 text-primary" />
              <CardTitle>Show Results Report</CardTitle>
            </div>
            <CardDescription>
              Open a printable summary with placement listings, show statistics, and awards ready to
              share with exhibitors.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CardFooter className="px-0 flex justify-end">
              <Button variant="outline" onClick={handleOpenShowResultsReport}>
                View Show Results Report
              </Button>
            </CardFooter>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <FileTextIcon className="w-5 h-5 text-sky-600" />
              <CardTitle>Show Results Display</CardTitle>
            </div>
            <CardDescription>
              Open a large-format layout for posting results by division with two classes per page.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CardFooter className="px-0 flex justify-end">
              <Button variant="outline" onClick={handleOpenDisplayReport}>
                View Display Layout
              </Button>
            </CardFooter>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <MegaphoneIcon className="w-5 h-5 text-emerald-600" />
              <CardTitle>Results Announcement</CardTitle>
            </div>
            <CardDescription>
              Open a printable announcement with stats, class placements, and special awards
              arranged for results readers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CardFooter className="px-0 flex justify-end">
              <Button variant="outline" onClick={handleOpenJudgeReport}>
                View Results Announcement
              </Button>
            </CardFooter>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <MedalIcon className="w-5 h-5 text-amber-600" />
              <CardTitle>Member Awards</CardTitle>
            </div>
            <CardDescription>
              Generate printable award cards and email AHSTC members their official results summary.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {memberAwardCardsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading member award data…</p>
            ) : memberAwardCardsQuery.isError ? (
              <p className="text-sm text-destructive">
                {memberAwardCardsQuery.error?.message ?? 'Failed to load member award data.'}
              </p>
            ) : memberPlacementsCount === 0 ? (
              <p className="text-sm text-muted-foreground">
                Finalize class placements to create cards and summaries for AHSTC members.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ready to print {memberPlacementsCount} card{memberPlacementsCount === 1 ? '' : 's'}{' '}
                for {memberRecipients.length} AHSTC member{memberRecipients.length === 1 ? '' : 's'}{' '}
                totaling {memberTotalPoints} points.
              </p>
            )}
          </CardContent>
          <CardFooter className="px-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {selectableRecipients.length} member{selectableRecipients.length === 1 ? '' : 's'}{' '}
              have valid email addresses.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                asChild
                variant="outline"
                className="w-full sm:w-auto"
                disabled={memberAwardCardsQuery.isLoading || memberPlacementsCount === 0}
              >
                <Link to={showAware('/reports/awards/cards', { currentSlug })}>
                  View Award Cards
                </Link>
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={handleOpenEmailSheet}
                disabled={
                  memberAwardCardsQuery.isLoading ||
                  memberPlacementsCount === 0 ||
                  selectableRecipients.length === 0 ||
                  sendMemberEmails.isPending
                }
              >
                {sendMemberEmails.isPending ? 'Preparing…' : 'Email AHSTC Members'}
              </Button>
            </div>
          </CardFooter>
        </Card>

        {currentShow.enableClubs !== false && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <UsersIcon className="w-5 h-5 text-amber-600" />
                <CardTitle>Club Participation</CardTitle>
              </div>
              <CardDescription>
                Measure exhibitor and entry contributions by club for{' '}
                <span className="font-semibold text-primary">{currentShow.name}</span>.
              </CardDescription>
            </CardHeader>
            <CardFooter className="px-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to={showAware('/reports/club-participation/view', { currentSlug })}>
                  View Club Participation
                </Link>
              </Button>
              <Button
                type="button"
                className="w-full sm:w-auto"
                variant="outline"
                onClick={handleOpenClubParticipationPrint}
              >
                Open Print View
              </Button>
            </CardFooter>
          </Card>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <UsersIcon className="w-5 h-5 text-sky-600" />
              <CardTitle>Staff Roster</CardTitle>
            </div>
            <CardDescription>
              Review a printable roster of the staff who worked on{' '}
              <span className="font-semibold text-primary">{currentShow.name}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CardFooter className="px-0 flex justify-end">
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                <Button
                  className="w-full sm:w-auto"
                  variant="outline"
                  onClick={handleOpenStaffRoster}
                >
                  View Staff Roster
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  variant="outline"
                  onClick={handleOpenStaffCertificates}
                >
                  Print Staff Certificates
                </Button>
              </div>
            </CardFooter>
          </CardContent>
        </Card>

        {/* Reports Generation Section */}
        <Card>
          <CardHeader>
            <CardTitle>Reports</CardTitle>
            <CardDescription>
              Generate and download CSV snapshots for{' '}
              <span className="font-semibold text-primary">{currentShow.name}</span>. We'll pull the
              latest exhibit results and finance totals for this show.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CardFooter className="px-0 flex justify-end">
              <Button
                onClick={handleGenerateReports}
                disabled={resultsCsv.isPending || financeCsv.isPending}
              >
                {resultsCsv.isPending || financeCsv.isPending ? 'Generating…' : 'Generate Reports'}
              </Button>
            </CardFooter>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <FileTextIcon className="h-5 w-5 text-blue-600" />
              <CardTitle>Report layout foundation</CardTitle>
            </div>
            <CardDescription>
              Preview the standardized printable layout, complete with AHSTC watermark, pagination,
              and Exhibit ID examples.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Use this demo when building future PDF or printed reports so every document shares
              consistent margins, header/footer treatments, and Exhibit ID terminology.
            </p>
          </CardContent>
          <CardFooter className="px-0">
            <Button asChild variant="outline">
              <Link to={showAware('/reports/layout-demo', { currentSlug })}>Open layout demo</Link>
            </Button>
          </CardFooter>
        </Card>

        {/* Results CSV Section */}
        {results && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <FileTextIcon className="w-5 h-5 text-blue-600" />
                <CardTitle>Results Report</CardTitle>
              </div>
              <CardDescription>
                Complete results data for all exhibits, classes, and awards in CSV format.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 border rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <pre className="whitespace-pre-wrap text-slate-800">{results}</pre>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => handleDownloadCsv(results, 'results.csv')}
                className="w-full"
              >
                Download Results CSV
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Finance CSV Section */}
        {finance && (
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <DollarSignIcon className="w-5 h-5 text-green-600" />
                <CardTitle>Finance Report</CardTitle>
              </div>
              <CardDescription>
                Financial summary including entry fees, payments, and revenue data in CSV format.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 border rounded-lg p-4 font-mono text-sm overflow-x-auto">
                <pre className="whitespace-pre-wrap text-slate-800">{finance}</pre>
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => handleDownloadCsv(finance, 'finance.csv')}
                className="w-full"
              >
                Download Finance CSV
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Error Message */}
        {(resultsCsv.isError || financeCsv.isError) && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2 text-red-800">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="font-medium">Error generating reports</span>
              </div>
              <p className="text-red-700 mt-1">
                {resultsCsv.error?.message ?? financeCsv.error?.message}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Sheet open={emailSheetOpen} onOpenChange={setEmailSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Email AHSTC members</SheetTitle>
            <SheetDescription>
              Choose which members receive their results summary before sending emails.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-6 overflow-y-auto px-4 pb-4">
            {memberAwardCardsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading member award data…</p>
            ) : memberAwardCardsQuery.isError ? (
              <p className="text-sm text-destructive">
                {memberAwardCardsQuery.error?.message ?? 'Failed to load member award data.'}
              </p>
            ) : memberRecipients.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Finalize class placements to email AHSTC members their results.
              </p>
            ) : (
              <div className="flex flex-col gap-6 lg:flex-row">
                <div className="space-y-3 lg:w-1/2">
                  <div className="text-sm font-semibold uppercase text-muted-foreground">
                    Recipients
                  </div>
                  <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                    {memberRecipients.map(recipient => {
                      const selected = selectedRecipientIds.has(recipient.exhibitorId)
                      const disabled = !recipient.email
                      return (
                        <label
                          key={recipient.exhibitorId}
                          className={
                            'flex gap-3 rounded-md border p-3 text-sm transition ' +
                            (selected
                              ? 'border-primary bg-primary/5'
                              : 'border-border bg-background') +
                            (disabled ? ' opacity-60' : '')
                          }
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4"
                            checked={selected}
                            disabled={disabled}
                            onChange={() => {
                              if (!disabled) handleRecipientToggle(recipient.exhibitorId)
                            }}
                          />
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold">{recipient.memberName}</span>
                              <span className="text-xs text-muted-foreground">
                                {recipient.totalPoints} pts
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              Exhibitor: {recipient.exhibitorName}
                            </span>
                            {recipient.staffRole ? (
                              <span className="text-xs text-muted-foreground">
                                Staff role: {recipient.staffRole}
                              </span>
                            ) : null}
                            {recipient.email ? (
                              <span className="text-xs text-muted-foreground">
                                {recipient.email}
                              </span>
                            ) : (
                              <span className="text-xs text-destructive">No email on file</span>
                            )}
                            <button
                              type="button"
                              className="text-xs font-medium text-primary underline-offset-2 hover:underline"
                              onClick={() => setPreviewExhibitorId(recipient.exhibitorId)}
                            >
                              Preview email
                            </button>
                          </div>
                        </label>
                      )
                    })}
                  </div>
                </div>
                <div className="space-y-3 lg:flex-1">
                  <div className="text-sm font-semibold uppercase text-muted-foreground">
                    Email preview
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto rounded-md border bg-background p-3 shadow-sm">
                    {previewExhibitorId ? (
                      emailPreviewQuery.isLoading ? (
                        <p className="text-sm text-muted-foreground">Loading preview…</p>
                      ) : emailPreviewQuery.isError ? (
                        <p className="text-sm text-destructive">
                          {emailPreviewQuery.error?.message ?? 'Failed to load preview.'}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          <div>
                            <div className="text-xs uppercase text-muted-foreground">Subject</div>
                            <p className="text-sm font-medium">{emailPreviewQuery.data?.subject}</p>
                          </div>
                          <div>
                            <div className="text-xs uppercase text-muted-foreground">Body</div>
                            <pre className="whitespace-pre-wrap rounded border bg-muted/30 p-3 text-sm leading-relaxed">
                              {emailPreviewQuery.data?.text}
                            </pre>
                          </div>
                        </div>
                      )
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Select a member to preview their email.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="gap-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Selected {memberEmailSelectedCount} of {selectableRecipients.length} email-ready
                member{selectableRecipients.length === 1 ? '' : 's'}.
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={handleSelectAllRecipients}
                  disabled={selectableRecipients.length === 0}
                >
                  Select all
                </Button>
                <Button
                  variant="outline"
                  onClick={handleClearRecipients}
                  disabled={memberEmailSelectedCount === 0}
                >
                  Clear
                </Button>
                <Button
                  onClick={handleSendMemberEmails}
                  disabled={sendMemberEmails.isPending || memberEmailSelectedCount === 0}
                >
                  {sendMemberEmails.isPending
                    ? 'Sending…'
                    : `Send ${memberEmailSelectedCount} email${memberEmailSelectedCount === 1 ? '' : 's'}`}
                </Button>
              </div>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
